import { z } from "zod";

import { featureFlagDefinitions } from "./flags";

export type FeatureFlagKey = keyof typeof featureFlagDefinitions;

export type FeatureFlags = {
  [K in FeatureFlagKey]: z.infer<(typeof featureFlagDefinitions)[K]["type"]>;
};

export type PartialFeatureFlags = Partial<FeatureFlags>;

export const FeatureFlagsSchema = z.object(
  Object.fromEntries(
    Object.entries(featureFlagDefinitions).map(([name, def]) => [
      name,
      def.type,
    ])
  )
);

export const PartialFeatureFlagsSchema = FeatureFlagsSchema.partial();

export const StrictPartialFeatureFlagsSchema =
  FeatureFlagsSchema.partial().strict();

/**
 * Parses a JSON string or object containing partial set of feature flag (i.e.
 * only overrides).
 */
export function parsePartialFeatureFlags(
  overridesOrJSON: unknown
): PartialFeatureFlags {
  const overrides =
    typeof overridesOrJSON === "string"
      ? JSON.parse(overridesOrJSON)
      : overridesOrJSON;

  return PartialFeatureFlagsSchema.parse(overrides);
}

/**
 * Like `parsePartialFeatureFlags`, but throws if any unknown keys are present.
 */
export function parsePartialFeatureFlagsStrict(
  overridesOrJSON: unknown
): PartialFeatureFlags {
  const overrides =
    typeof overridesOrJSON === "string"
      ? JSON.parse(overridesOrJSON)
      : overridesOrJSON;

  return StrictPartialFeatureFlagsSchema.parse(overrides);
}

/**
 * Parses a JSON string or object containing a partial set of feature flags (
 * i.e. only overrides), and fills in the rest with the default values.
 */
export function parseFeatureFlags(
  overridesOrJSON: unknown,
  defaults: PartialFeatureFlags
): FeatureFlags {
  const overrides = parsePartialFeatureFlags(overridesOrJSON) as any;
  for (const key of Object.keys(featureFlagDefinitions) as FeatureFlagKey[]) {
    // we do this instead of {...defaultFeatureFlags, ...overrides} to overwrite
    // "undefined" values with the default
    if (overrides[key] === undefined) {
      overrides[key] = defaults[key];
    }
  }

  return overrides;
}
